import { sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { SudoContextOrganization, SudoContextUser } from '@/store/sudo'
import { userSchema } from '@/validation-schemas'
import { Button } from '@repo/ui/button'
import { Checkbox } from '@repo/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { ShieldAlert } from 'lucide-react'
import { useEffect, useMemo } from 'react'

export function UserEditor({
  modalHandler,
  user,
}: {
  modalHandler: { open: boolean; setOpen: (v: boolean) => void }
  user: SudoContextUser
  organizations: SudoContextOrganization[]
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT({ keyPrefix: 'manageUsers.modal' })

  const defaultValues = useMemo(
    () => ({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      superAdmin: user.superAdmin,
    }),
    [user],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } =
    useFormValidator({
      defaultValues,
      onSubmit: (values) => {
        modalHandler.setOpen(false)
        void dispatch(
          sudoStoreActions.user.update({
            fields: values,
            userId: user.id,
          }),
        )
      },
      schema: {
        firstName: userSchema.firstName,
        lastName: userSchema.lastName,
        email: userSchema.email,
        superAdmin: userSchema.superAdmin,
      },
    })

  useEffect(() => {
    if (modalHandler.open) form.reset()
  }, [modalHandler.open])

  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent className="max-h-screen overflow-y-auto sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Make changes to the user here. Click update when you're done.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={submit}
            className="space-y-4"
            onKeyDown={submitOnPressMetaEnter}
          >
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('firstName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={tc('firstName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('lastName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={tc('lastName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('email')}</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder={tc('email')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="superAdmin"
              render={({ field }) => (
                <FormItem>
                  <div className="border-primary-200 flex items-center gap-2 rounded-md border p-3">
                    <div className="flex-shrink-0">
                      <ShieldAlert className="text-primary-600 h-6 w-6" />
                    </div>
                    <FormControl>
                      <Checkbox
                        id="superAdmin"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel htmlFor="superAdmin" className="cursor-pointer">
                      <strong className="text-highlight">Caution:</strong> check
                      this box to give this user the{' '}
                      <strong>CaseAI-Super-Admin</strong> power.
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={!isFormValid}>
                {tc('update', { cfl: true })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
